import { getUniqueId } from "@jugl-web/utils";
import { WithApiRequestMeta } from "../../api-results";
import {
  PaginatedRequestParams,
  PaginatedResponse,
  QueryParamsWithEntityId,
  RtkEmptySplitApi,
} from "../../types";
import {
  CRMConfig,
  WebChatBotConversationDto,
  WhatsappAgentModel,
  WhatsappMessageModel,
  Workflow,
  WorkflowLog,
  WorkflowParams,
} from "./models";

import { InstagramAccountModel } from "./models/InstagramAccount";
import {
  InstagramConversationModel,
  transformInstagramConversationDtoToModel,
} from "./models/InstagramConversation";
import { InstagramConversationDto } from "./models/InstagramConversation/InstagramConversationDto";
import { InstagramMessageModel } from "./models/InstagramMessage/InstagramMessageModel";
import { TwilioAccountModel } from "./models/TwilioAccount";
import { TwilioPhoneNumberModel } from "./models/TwilioPhoneNumber";
import { VoiceAccountMetadata, VoiceAccountModel } from "./models/VoiceAccount";
import {
  TwilioMessageDto,
  TwilioMessageModel,
  transformTwilioMessageDtoToModel,
} from "./models/TwilioMessage";
import { LiveChatAppAccountModel } from "./models/LiveChatAppAccount/LiveChatAppAccountModel";
import { WebChatBotAccountModel } from "./models/WebChatBotAccount/WebChatBotAccountModel";
import { WebChatBotConversationModel } from "./models/WebChatBotConversation/WebChatBotConversationModel";
import { transformWebChatBotConversationDtoToModel } from "./models/WebChatBotConversation/utils";
import { WebChatBotMessageDto } from "./models/WebChatBotMessage/WebChatBotMessageDto";
import { WebChatBotMessageModel } from "./models/WebChatBotMessage/WebChatBotMessageModel";
import { transformWebChatBotMessageDtoToModel } from "./models/WebChatBotMessage/utils";
import { WhatsappAgentLogModel } from "./models/WhatsappAgentLog";
import {
  WhatsappConversationModel,
  transformWhatsappConversationDtoToModel,
} from "./models/WhatsappConversation";
import { WhatsappConversationDto } from "./models/WhatsappConversation/WhatsappConversationDto";
import {
  WhatsappTemplateDto,
  WhatsappTemplateModel,
  transformWhatsappTemplateDtoToModel,
} from "./models/WhatsappTemplate";
import { IntegrationsApiTag } from "./tags";
import {
  AgentConfigData,
  AgentConfigDto,
  AIAgentInsights,
  CRMChannel,
  CRMConversationFilter,
  CRMConversationMode,
  CRMConversationStatus,
  CRMUnreadCounts,
  SendEmailMessageMutationParams,
  SendInstagramMessageMutationParams,
  SendTwilioMessageMutationParams,
  SendWhatsappMessageMutationParams,
  WhatsappBusinessVerificationStatus,
  WhatsappProfile,
  WhatsappQualityRating,
  WhatsappTemplateCategory,
  WhatsappTemplatePayload,
  WhatsappTemplateStatus,
  CalendlyEventType,
  ConnectionDto,
  ConnectionType,
  SquareupLocation,
  CalcomEventType,
} from "./types";
import { AIAgentAnalyticsItemDto } from "./models/AIAgentAnalyticsItem/AIAgentAnalyticsItemDto";
import { AIAgentAnalyticsItemModel } from "./models/AIAgentAnalyticsItem/AIAgentAnalyticsItemModel";
import { transformAIAgentAnalyticsItemsDtoToModels } from "./models/AIAgentAnalyticsItem/utils";
import {
  TwilioConversationDto,
  transformTwilioConversationDtoToModel,
  TwilioConversationModel,
} from "./models/TwilioConversation";
import {
  VoiceConversationDto,
  transformVoiceConversationDtoToModel,
  VoiceConversationModel,
} from "./models/VoiceConversation";
import {
  VoiceConversationHistoryItemDto,
  VoiceConversationHistoryItemModel,
  transformVoiceConversationHistoryItemDtoToModel,
} from "./models/VoiceConversationHistory";
import {
  VoiceMessageDto,
  VoiceMessageModel,
  transformVoiceMessageDtoToModel,
} from "./models/VoiceMessage";
import {
  EmailAccountModel,
  UpdateEmailConfigPayload,
} from "./models/EmailAccount";
import {
  EmailMessageDto,
  EmailMessageModel,
  transformEmailMessageDtoToModel,
} from "./models/EmailMessage";
import {
  EmailConversationDto,
  transformEmailConversationDtoToModel,
  EmailConversationModel,
} from "./models/EmailConversation";

export const addIntegrationsApi = (emptySplitApi: RtkEmptySplitApi) => {
  const apiWithTags = emptySplitApi.enhanceEndpoints({
    addTagTypes: [
      IntegrationsApiTag.whatsappMessageMedia,
      IntegrationsApiTag.whatsappTemplate,
      IntegrationsApiTag.crmConfig,
      IntegrationsApiTag.whatsappProfile,
      IntegrationsApiTag.workflowLogs,
      IntegrationsApiTag.instagramMessage,
      IntegrationsApiTag.shopifyAccount,
      IntegrationsApiTag.calendlyConnection,
      IntegrationsApiTag.calcomConnection,
      IntegrationsApiTag.squareupConnection,
      IntegrationsApiTag.agentConfig,
      IntegrationsApiTag.crmUnreadCounts,
      IntegrationsApiTag.emailMessageMedia,
    ],
  });

  const integrationsApi = apiWithTags.injectEndpoints({
    overrideExisting: false,
    endpoints: (builder) => ({
      // WhatsApp

      getWhatsappTemplates: builder.query<
        {
          data: WhatsappTemplateModel[];
          next_page: string | null;
        },
        WithApiRequestMeta<{
          entityId: string;
          params?: {
            type?: WhatsappTemplateCategory;
            status?: WhatsappTemplateStatus;
            enabled?: boolean;
            page?: string;
          };
        }>
      >({
        query: ({ entityId, params }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/template`,
          params,
        }),
        transformResponse: (response: {
          data: WhatsappTemplateDto[];
          next_page: string | null;
        }) => ({
          ...response,
          data: response.data.map(transformWhatsappTemplateDtoToModel),
        }),
      }),
      createWhatsappTemplate: builder.mutation<
        WhatsappTemplateModel,
        WithApiRequestMeta<{ entityId: string; data: WhatsappTemplatePayload }>
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/template`,
          method: "POST",
          // TODO: temp solution until backend picks up
          data: {
            ...data,
            category:
              data.type === WhatsappTemplateCategory.message
                ? "MARKETING"
                : "UTILITY",
            action: data.action || getUniqueId(),
          },
        }),
        transformResponse: (response: WhatsappTemplateDto) =>
          transformWhatsappTemplateDtoToModel(response),
      }),
      deleteWhatsappTemplate: builder.mutation<
        void,
        WithApiRequestMeta<{ entityId: string; templateId: string }>
      >({
        query: ({ entityId, templateId }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/template/${templateId}`,
          method: "DELETE",
        }),
      }),
      getWhatsappTemplate: builder.query<
        WhatsappTemplateModel,
        WithApiRequestMeta<{ entityId: string; templateId: string }>
      >({
        query: ({ entityId, templateId }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/template/${templateId}`,
        }),
        transformResponse: (response: WhatsappTemplateDto) =>
          transformWhatsappTemplateDtoToModel(response),
        providesTags: (_result, _error, { templateId }) => [
          { type: IntegrationsApiTag.whatsappTemplate, id: templateId },
        ],
      }),
      updateWhatsappTemplate: builder.mutation<
        WhatsappTemplateModel,
        WithApiRequestMeta<{
          entityId: string;
          data: WhatsappTemplatePayload;
          templateId: string;
        }>
      >({
        query: ({ entityId, data, templateId }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/template/${templateId}`,
          method: "PUT",
          data,
        }),
        transformResponse: (response: WhatsappTemplateDto) =>
          transformWhatsappTemplateDtoToModel(response),
        invalidatesTags: (result, error, { templateId }) =>
          result
            ? [{ type: IntegrationsApiTag.whatsappTemplate, id: templateId }]
            : [],
      }),
      connectWhatsappAgentAccount: builder.mutation<
        WhatsappAgentModel,
        WithApiRequestMeta<{
          entityId: string;
          data: {
            code: string;
            waba_id: string;
            phone_number_id: string;
          };
        }>
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/account`,
          method: "POST",
          data: { ...data, redirect_url: "" },
        }),
        invalidatesTags: [IntegrationsApiTag.crmConfig],
      }),
      disconnectWhatsappAgentAccount: builder.mutation<
        void,
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/account`,
          method: "DELETE",
        }),
        invalidatesTags: [IntegrationsApiTag.crmConfig],
      }),
      changeWhatsappTemplateStatus: builder.mutation<
        void,
        WithApiRequestMeta<{
          entityId: string;
          templateId: string;
          isEnabled: boolean;
        }>
      >({
        query: ({ entityId, templateId, isEnabled }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/template/${templateId}`,
          method: "PUT",
          data: {
            enabled: isEnabled,
          },
        }),
      }),
      getAgentLogs: builder.query<
        PaginatedResponse<WhatsappAgentLogModel>,
        WithApiRequestMeta<PaginatedRequestParams<{ entityId: string }>>
      >({
        query: ({ entityId, meta: _meta, ...params }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/logs`,
          params: {
            ...params,
          },
        }),
      }),
      registerWhatsappAgentAccount: builder.mutation<
        { success: boolean },
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/account/register`,
          method: "POST",
        }),
        invalidatesTags: [IntegrationsApiTag.crmConfig],
      }),
      getWhatsappConversations: builder.query<
        PaginatedResponse<WhatsappConversationModel>,
        WithApiRequestMeta<
          PaginatedRequestParams<{
            entityId: string;
            filter:
              | CRMConversationFilter
              | CRMConversationMode.onAIAutoReply
              | CRMConversationMode.waitingResponse
              | string;
            search_term?: string;
          }>
        >
      >({
        query: ({ entityId, filter, page, page_size, search_term }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/conversation`,
          params: {
            filter,
            page,
            page_size,
            search_term: search_term?.trim() || undefined,
          },
        }),
        transformResponse: (
          response: PaginatedResponse<WhatsappConversationDto>
        ) => ({
          ...response,
          data: response.data.map(transformWhatsappConversationDtoToModel),
        }),
      }),
      getWhatsappConversationById: builder.query<
        WhatsappConversationModel,
        WithApiRequestMeta<{ entityId: string; convId: string }>
      >({
        query: ({ entityId, convId }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/conversation/${convId}`,
        }),
        transformResponse: (response: WhatsappConversationDto) =>
          transformWhatsappConversationDtoToModel(response),
        keepUnusedDataFor: 0,
      }),
      getBlockedContacts: builder.query<
        string[],
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/blocked`,
        }),
        transformResponse: (response: {
          data: {
            messaging_product: "whatsapp";
            wa_id: string;
          }[];
        }) => response.data.map((item) => item.wa_id),
      }),
      manageContact: builder.mutation<
        void,
        WithApiRequestMeta<{
          entityId: string;
          userId: string;
          type: "block" | "unblock";
        }>
      >({
        query: ({ entityId, userId, type }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/blocked`,
          method: type === "block" ? "POST" : "DELETE",
          data: {
            users: [{ user: userId }],
          },
        }),
        onQueryStarted: (args, { dispatch, queryFulfilled }) => {
          const updateBlockedContacts = dispatch(
            integrationsApi.util.updateQueryData(
              "getBlockedContacts",
              {
                entityId: args.entityId,
              },
              (blockedContactsDraft) => {
                if (args.type === "block") {
                  return [...blockedContactsDraft, args.userId];
                }

                return blockedContactsDraft.filter(
                  (blockedContact) => blockedContact !== args.userId
                );
              }
            )
          );
          queryFulfilled.catch(() => {
            updateBlockedContacts.undo();
          });
        },
      }),
      updateConversationStatus: builder.mutation<
        void,
        WithApiRequestMeta<{
          entityId: string;
          channel: CRMChannel;
          conv_id: string;
          status: CRMConversationStatus;
        }>
      >({
        query: ({ entityId, channel, conv_id, status }) => ({
          url: `/api/auth/${entityId}/agents/crm/conversation`,
          method: "PUT",
          data: {
            channel,
            conv_id,
            status,
          },
        }),
      }),
      getWhatsappMessages: builder.query<
        PaginatedResponse<WhatsappMessageModel>,
        WithApiRequestMeta<{
          entityId: string;
          params: PaginatedRequestParams<{
            conv_id: string;
            from: string;
          }>;
        }>
      >({
        queryFn: async (arg, api, extraOptions, baseQuery) => {
          const { entityId, params } = arg;
          const result = await baseQuery({
            url: `/api/auth/${entityId}/agents/whatsapp/message`,
            params,
          });

          if (result.error) return { error: result.error };

          const data = result.data as PaginatedResponse<WhatsappMessageModel>;

          // Process messages to load replies if they have context.id
          const messagesWithReplies = await Promise.all(
            data.data.map(async (message) => {
              if (message.payload.context?.id) {
                try {
                  const replyResult = await baseQuery({
                    url: `/api/auth/${entityId}/agents/whatsapp/msg_id`,
                    method: "POST",
                    data: {
                      msg_id: message.payload.context.id,
                    },
                  });
                  if (replyResult.data?.data?.[0]) {
                    return {
                      ...message,
                      reply: replyResult.data.data[0] as WhatsappMessageModel,
                    };
                  }
                } catch (error) {
                  // oxlint-disable-next-line no-console
                  console.error("Failed to load reply message:", error);
                }
              }
              return message;
            })
          );

          return {
            data: {
              ...data,
              data: messagesWithReplies,
            },
          };
        },
      }),
      sendWhatsappMessage: builder.mutation<
        WhatsappMessageModel,
        SendWhatsappMessageMutationParams
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/message`,
          method: "POST",
          data,
        }),
      }),
      getWhatsappProfile: builder.query<
        WhatsappProfile,
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/profile`,
        }),
        transformResponse: (response: { data: WhatsappProfile[] }) =>
          response.data[0],
        providesTags: [IntegrationsApiTag.whatsappProfile],
      }),
      updateWhatsappProfile: builder.mutation<
        void,
        WithApiRequestMeta<{
          entityId: string;
          profile?: Omit<WhatsappProfile, "profile_picture_url">;
          img?: File;
        }>
      >({
        query: ({ entityId, profile, img }) => {
          const formData = new FormData();
          if (profile) {
            formData.append("profile", JSON.stringify(profile));
          }
          if (img) {
            formData.append("display_pic", img);
          }

          return {
            url: `/api/auth/${entityId}/agents/whatsapp/profile`,
            method: "POST",
            data: formData,
          };
        },
        onQueryStarted: (args, { dispatch, queryFulfilled }) => {
          const updateWhatsappProfileResult = dispatch(
            integrationsApi.util.updateQueryData(
              "getWhatsappProfile",
              {
                entityId: args.entityId,
              },
              (whatsappProfileDraft) => ({
                ...whatsappProfileDraft,
                ...args.profile,
              })
            )
          );
          queryFulfilled.catch(() => {
            updateWhatsappProfileResult.undo();
          });
        },
        invalidatesTags: (e1, e2, args) =>
          args.img ? [IntegrationsApiTag.whatsappProfile] : [],
      }),
      setWhatsappPin: builder.mutation<
        void,
        WithApiRequestMeta<{
          entityId: string;
          pin: string;
        }>
      >({
        query: ({ entityId, pin }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/2FA`,
          method: "POST",
          data: { pin },
        }),
      }),
      updateWhatsappDisplayName: builder.mutation<
        void,
        WithApiRequestMeta<{
          entityId: string;
          name: string;
        }>
      >({
        query: ({ entityId, name }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/display_name`,
          method: "POST",
          data: { name },
        }),
      }),
      registerWhatsappAction: builder.mutation<
        void,
        WithApiRequestMeta<{
          entityId: string;
          pin: string;
        }>
      >({
        query: ({ entityId, pin }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/account/register`,
          method: "POST",
          data: { pin },
        }),
      }),
      getWhatsappQualityRating: builder.query<
        { rating: WhatsappQualityRating },
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/quality_rating`,
        }),
      }),
      getWhatsappBusinessVerificationStatus: builder.query<
        { status: WhatsappBusinessVerificationStatus },
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/verification_status`,
        }),
      }),
      getWhatsappSettings: builder.query<
        void,
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/settings`,
        }),
      }),
      updateWhatsappAgentAccount: builder.mutation<
        WhatsappAgentModel | null,
        WithApiRequestMeta<{ entityId: string; hideNumber: boolean }>
      >({
        query: ({ entityId, hideNumber }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/settings`,
          method: "POST",
          data: {
            settings: { hide_num: hideNumber },
          },
        }),
        onQueryStarted: (args, { dispatch, queryFulfilled }) => {
          const updatewhatsappAgentAccount = dispatch(
            integrationsApi.util.updateQueryData(
              "getCRMConfig",
              {
                entityId: args.entityId,
              },
              (crmConfigDraft) => ({
                ...crmConfigDraft,
                whatsapp: crmConfigDraft.whatsapp
                  ? {
                      ...crmConfigDraft.whatsapp,
                      settings: {
                        hide_num: args.hideNumber,
                      },
                    }
                  : null,
              })
            )
          );
          queryFulfilled.catch(() => {
            updatewhatsappAgentAccount.undo();
          });
        },
        transformResponse: (
          data: WhatsappAgentModel | Record<string, never>
        ) =>
          Object.keys(data).length === 0 ? null : (data as WhatsappAgentModel),
      }),
      uploadWhatsappMedia: builder.mutation<
        { id: string },
        WithApiRequestMeta<{ entityId: string; file: File }>
      >({
        query: ({ entityId, file }) => {
          const formData = new FormData();
          formData.append("file", file);
          return {
            url: `/api/auth/${entityId}/agents/whatsapp/media`,
            method: "POST",
            data: formData,
            headers: { "Content-Type": "multipart/form-data" },
          };
        },
      }),
      getWhatsappMessageById: builder.query<
        { data: WhatsappMessageModel[] },
        WithApiRequestMeta<{ entityId: string; params: { msg_id: string } }>
      >({
        query: ({ entityId, params }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/msg_id`,
          data: params,
          method: "POST",
        }),
      }),
      sendWhatsappMessageReaction: builder.mutation<
        void,
        WithApiRequestMeta<{
          entityId: string;
          data: {
            to: string;
            conv_name: string;
            type: "reaction";
            payload: {
              message_id: string;
              emoji: string;
            };
          };
        }>
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/message`,
          method: "POST",
          data,
        }),
      }),
      getWhatsappMessageMedia: builder.query<
        Blob,
        WithApiRequestMeta<{ entityId: string; mediaId: string }>
      >({
        query: ({ entityId, mediaId }) => ({
          url: `/api/auth/${entityId}/agents/whatsapp/media`,
          params: { id: mediaId },
          responseType: "blob",
        }),
        providesTags: (result, error, { mediaId }) => [
          { type: IntegrationsApiTag.whatsappMessageMedia, id: mediaId },
        ],
      }),

      // Instagram

      getInstagramConversations: builder.query<
        PaginatedResponse<InstagramConversationModel>,
        WithApiRequestMeta<
          QueryParamsWithEntityId<
            PaginatedRequestParams<{
              search_term?: string;
              filter:
                | CRMConversationFilter.active
                | CRMConversationFilter.expired
                | CRMConversationFilter.closed
                | CRMConversationMode.onAIAutoReply
                | CRMConversationMode.waitingResponse
                | string;
            }>
          >
        >
      >({
        query: ({ entityId, params }) => ({
          url: `/api/auth/${entityId}/agents/instagram/conversation`,
          params: {
            ...params,
            search_term: params?.search_term?.trim() || undefined,
          },
        }),
        transformResponse: (
          response: PaginatedResponse<InstagramConversationDto>
        ) => ({
          ...response,
          data: response.data.map(transformInstagramConversationDtoToModel),
        }),
      }),
      getInstagramConversationById: builder.query<
        InstagramConversationModel,
        WithApiRequestMeta<{ entityId: string; convId: string }>
      >({
        query: ({ entityId, convId }) => ({
          url: `/api/auth/${entityId}/agents/instagram/conversation/${convId}`,
        }),
        transformResponse: (response: InstagramConversationDto) =>
          transformInstagramConversationDtoToModel(response),
        keepUnusedDataFor: 0,
      }),
      getInstagramMessages: builder.query<
        PaginatedResponse<InstagramMessageModel>,
        WithApiRequestMeta<{
          entityId: string;
          params: PaginatedRequestParams<{
            conv_id: string;
            from: string;
          }>;
        }>
      >({
        query: ({ entityId, params }) => ({
          url: `/api/auth/${entityId}/agents/instagram/message`,
          params,
        }),
      }),
      sendInstagramMessage: builder.mutation<
        InstagramMessageModel,
        WithApiRequestMeta<SendInstagramMessageMutationParams>
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/agents/instagram/message`,
          method: "POST",
          data,
        }),
      }),
      getInstagramMessageById: builder.query<
        { data: InstagramMessageModel[] },
        WithApiRequestMeta<{ entityId: string; params: { msg_id: string } }>
      >({
        query: ({ entityId, params }) => ({
          url: `/api/auth/${entityId}/agents/msg_id`,
          data: {
            ...params,
            module: "instagram",
          },
          method: "POST",
        }),
        providesTags: (e1, e2, { params: { msg_id } }) => [
          { type: IntegrationsApiTag.instagramMessage, id: msg_id },
        ],
      }),
      connectInstagramAccount: builder.mutation<
        InstagramAccountModel,
        WithApiRequestMeta<{
          entityId: string;
          data: {
            code: string;
            redirect_uri: string;
          };
        }>
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/agents/instagram/connect`,
          method: "POST",
          data: { ...data },
        }),
        invalidatesTags: [IntegrationsApiTag.crmConfig],
      }),
      disconnectInstagramAccount: builder.mutation<
        void,
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          method: "POST",
          url: `/api/auth/${entityId}/agents/instagram/disconnect`,
        }),
        invalidatesTags: [IntegrationsApiTag.crmConfig],
      }),
      getInstagramMessageMedia: builder.query<
        Blob,
        WithApiRequestMeta<{ entityId: string; mediaId: string }>
      >({
        query: ({ entityId, mediaId }) => ({
          url: `/api/auth/${entityId}/agents/instagram/media`,
          params: { id: mediaId },
          responseType: "blob",
        }),
      }),
      uploadInstagramMedia: builder.mutation<
        { media_id: string; url: string },
        WithApiRequestMeta<{ entityId: string; file: File }>
      >({
        query: ({ entityId, file }) => {
          const formData = new FormData();
          formData.append("file", file);
          return {
            url: `/api/auth/${entityId}/agents/instagram/media`,
            method: "POST",
            data: formData,
            headers: { "Content-Type": "multipart/form-data" },
          };
        },
      }),
      sendInstagramMessageReaction: builder.mutation<
        void,
        WithApiRequestMeta<{
          entityId: string;
          data: {
            payload: {
              recipient: {
                id: string;
              };
              sender_action: "react" | "unreact";
              payload: {
                message_id: string;
                reaction?: string;
              };
            };
          };
        }>
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/agents/instagram/message`,
          method: "POST",
          data,
        }),
      }),

      // Web Chat Bot

      getWebChatBotConversations: builder.query<
        PaginatedResponse<WebChatBotConversationModel>,
        WithApiRequestMeta<
          PaginatedRequestParams<{
            entityId: string;
            filter?:
              | CRMConversationFilter.active
              | CRMConversationFilter.closed
              | CRMConversationMode.onAIAutoReply
              | CRMConversationMode.waitingResponse
              | string;
            search_term?: string;
          }>
        >
      >({
        query: ({ entityId, page, page_size, search_term, filter }) => ({
          url: `/api/auth/${entityId}/agents/chatbot/conversation`,
          params: {
            page,
            page_size,
            filter,
            search_term: search_term?.trim() || undefined,
          },
        }),
        transformResponse: (
          response: PaginatedResponse<WebChatBotConversationDto>
        ) => ({
          ...response,
          data: response.data.map(transformWebChatBotConversationDtoToModel),
        }),
      }),
      getWebChatBotConversationById: builder.query<
        WebChatBotConversationModel,
        WithApiRequestMeta<{ entityId: string; convId: string }>
      >({
        query: ({ entityId, convId }) => ({
          url: `/api/auth/${entityId}/agents/chatbot/conversation/${convId}`,
        }),
        transformResponse: (response: WebChatBotConversationDto) =>
          transformWebChatBotConversationDtoToModel(response),
        keepUnusedDataFor: 0,
      }),
      getWebChatBotMessages: builder.query<
        PaginatedResponse<WebChatBotMessageModel>,
        WithApiRequestMeta<{
          entityId: string;
          params: PaginatedRequestParams<{
            conv_id: string;
          }>;
        }>
      >({
        query: ({ entityId, params }) => ({
          url: `/api/auth/${entityId}/agents/chatbot/message`,
          params,
        }),
        transformResponse: (
          response: PaginatedResponse<WebChatBotMessageDto>
        ) => ({
          ...response,
          data: response.data.map(transformWebChatBotMessageDtoToModel),
        }),
      }),
      getChatBotToken: builder.mutation<
        { token: string },
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/agents/chatbot/token`,
        }),
        invalidatesTags: [IntegrationsApiTag.crmConfig],
      }),
      getWebChatBotConfig: builder.query<
        WebChatBotAccountModel,
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/agents/chatbot/config`,
        }),
        providesTags: [IntegrationsApiTag.crmConfig],
      }),
      updateWebChatBotConfig: builder.mutation<
        WebChatBotAccountModel,
        WithApiRequestMeta<{
          entityId: string;
          settings?: Record<string, unknown>;
          enabled?: boolean;
        }>
      >({
        query: ({ entityId, settings, enabled }) => ({
          url: `/api/auth/${entityId}/agents/chatbot/config`,
          method: "PUT",
          data: {
            settings,
            enabled,
          },
        }),
        onQueryStarted: (args, { dispatch, queryFulfilled }) => {
          const updateWebChatBotConfigResult = dispatch(
            integrationsApi.util.updateQueryData(
              "getCRMConfig",
              {
                entityId: args.entityId,
              },
              (crmConfigDraft) => ({
                ...crmConfigDraft,
                chatbot: crmConfigDraft.chatbot
                  ? {
                      ...crmConfigDraft.chatbot,
                      settings:
                        args.settings ?? crmConfigDraft.chatbot.settings,
                      enabled: args.enabled ?? crmConfigDraft.chatbot.enabled,
                    }
                  : null,
              })
            )
          );
          const updateGetWebChatBotConfigResult = dispatch(
            integrationsApi.util.updateQueryData(
              "getWebChatBotConfig",
              {
                entityId: args.entityId,
              },
              (webChatBotConfigDraft) => ({
                ...webChatBotConfigDraft,
                settings: args.settings ?? webChatBotConfigDraft.settings,
                enabled: args.enabled ?? webChatBotConfigDraft.enabled,
              })
            )
          );
          queryFulfilled.catch(() => {
            updateWebChatBotConfigResult.undo();
            updateGetWebChatBotConfigResult.undo();
          });
        },
      }),
      uploadChatBotFiles: builder.mutation<
        { status: string },
        WithApiRequestMeta<{
          entityId: string;
          files: {
            file: File;
            type:
              | "launcher_icon"
              | "avatar_icon"
              | "chat_icon"
              | "welcome_msg_media";
          }[];
        }>
      >({
        query: ({ entityId, files }) => {
          const formData = new FormData();

          files.forEach(({ file, type }) => {
            formData.append(`files[${type}]`, file);
          });

          return {
            url: `/api/auth/${entityId}/agents/chatbot/upload_files`,
            method: "POST",
            headers: { "Content-Type": "multipart/form-data" },
            data: formData,
          };
        },
      }),
      disconnectWebChatBot: builder.mutation<
        void,
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/agents/chatbot/disconnect`,
          method: "POST",
        }),
        invalidatesTags: [IntegrationsApiTag.crmConfig],
      }),

      // Live Chat App

      getLiveChatAppConfig: builder.query<
        LiveChatAppAccountModel,
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/agents/livechat/config`,
        }),
        providesTags: [IntegrationsApiTag.crmConfig],
      }),
      updateLiveChatAppConfig: builder.mutation<
        LiveChatAppAccountModel,
        WithApiRequestMeta<{
          entityId: string;
          settings?: Partial<Record<string, unknown>>;
          enabled?: boolean;
        }>
      >({
        query: ({ entityId, settings, enabled }) => ({
          url: `/api/auth/${entityId}/agents/livechat/config`,
          method: "PUT",
          data: {
            settings,
            enabled,
          },
        }),
        onQueryStarted: (args, { dispatch, queryFulfilled }) => {
          const updateCRMConfigResult = dispatch(
            integrationsApi.util.updateQueryData(
              "getCRMConfig",
              {
                entityId: args.entityId,
              },
              (crmConfigDraft) => ({
                ...crmConfigDraft,
                livechat: crmConfigDraft.livechat
                  ? {
                      ...crmConfigDraft.livechat,
                      settings:
                        args.settings ?? crmConfigDraft.livechat.settings,
                      enabled: args.enabled ?? crmConfigDraft.livechat.enabled,
                    }
                  : null,
              })
            )
          );
          const updateGetLiveChatAppConfigResult = dispatch(
            integrationsApi.util.updateQueryData(
              "getLiveChatAppConfig",
              {
                entityId: args.entityId,
              },
              (liveChatAppConfigDraft) => ({
                ...liveChatAppConfigDraft,
                settings: args.settings ?? liveChatAppConfigDraft.settings,
                enabled: args.enabled ?? liveChatAppConfigDraft.enabled,
              })
            )
          );
          queryFulfilled.catch(() => {
            updateCRMConfigResult.undo();
            updateGetLiveChatAppConfigResult.undo();
          });
        },
      }),
      uploadLiveChatAppFiles: builder.mutation<
        { status: string },
        WithApiRequestMeta<{
          entityId: string;
          files: {
            file: File;
            type:
              | "launcher_icon"
              | "avatar_icon"
              | "chat_icon"
              | "welcome_msg_media";
          }[];
        }>
      >({
        query: ({ entityId, files }) => {
          const formData = new FormData();

          files.forEach(({ file, type }) => {
            formData.append(`files[${type}]`, file);
          });

          return {
            url: `/api/auth/${entityId}/agents/livechat/upload_files`,
            method: "POST",
            headers: { "Content-Type": "multipart/form-data" },
            data: formData,
          };
        },
      }),

      // Integration

      getWorkatoEmbeddedUrl: builder.query<
        { url: string },
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/agents/workato/embedded/${entityId}`,
        }),
      }),

      getTwilioConversations: builder.query<
        PaginatedResponse<TwilioConversationModel>,
        WithApiRequestMeta<
          PaginatedRequestParams<{
            entityId: string;
            search_term: string;
            filter?:
              | CRMConversationFilter.active
              | CRMConversationFilter.closed
              | CRMConversationMode.onAIAutoReply
              | CRMConversationMode.waitingResponse
              | string;
          }>
        >
      >({
        query: ({ entityId, page, page_size, filter, search_term }) => ({
          url: `/api/auth/${entityId}/agents/twilio/conversation`,
          params: {
            page,
            page_size,
            filter,
            search_term: search_term?.trim() || undefined,
          },
        }),
        transformResponse: (
          response: PaginatedResponse<TwilioConversationDto>
        ) => ({
          ...response,
          data: response.data.map(transformTwilioConversationDtoToModel),
        }),
      }),

      getTwilioConversationById: builder.query<
        TwilioConversationModel,
        WithApiRequestMeta<{ entityId: string; convId: string }>
      >({
        query: ({ entityId, convId }) => ({
          url: `/api/auth/${entityId}/agents/twilio/conversation/${convId}`,
        }),
        transformResponse: (response: TwilioConversationDto) =>
          transformTwilioConversationDtoToModel(response),
        keepUnusedDataFor: 0,
      }),
      getTwilioMessages: builder.query<
        PaginatedResponse<TwilioMessageModel>,
        WithApiRequestMeta<{
          entityId: string;
          params: PaginatedRequestParams<{
            conv_id: string;
          }>;
        }>
      >({
        query: ({ entityId, params }) => ({
          url: `/api/auth/${entityId}/agents/twilio/message`,
          params,
        }),
        transformResponse: (response: PaginatedResponse<TwilioMessageDto>) => ({
          ...response,
          data: response.data.map(transformTwilioMessageDtoToModel),
        }),
      }),

      getVoiceConversations: builder.query<
        PaginatedResponse<VoiceConversationModel>,
        PaginatedRequestParams<{
          entityId: string;
          search_term: string;
        }>
      >({
        query: ({ entityId, page, page_size, search_term }) => ({
          url: `/api/auth/${entityId}/agents/twilio/conversation`,
          params: {
            page,
            page_size,
            search_term: search_term?.trim() || undefined,
            type: "voice",
          },
        }),
        transformResponse: (
          response: PaginatedResponse<VoiceConversationDto>
        ) => ({
          ...response,
          data: response.data.map(transformVoiceConversationDtoToModel),
        }),
      }),

      getVoiceConversationById: builder.query<
        VoiceConversationModel,
        { entityId: string; convId: string }
      >({
        query: ({ entityId, convId }) => ({
          url: `/api/auth/${entityId}/agents/voice/conversation/${convId}`,
        }),
        transformResponse: (response: VoiceConversationDto) =>
          transformVoiceConversationDtoToModel(response),
        keepUnusedDataFor: 0,
      }),

      getVoiceMessages: builder.query<
        PaginatedResponse<VoiceMessageModel>,
        {
          entityId: string;
          params: PaginatedRequestParams<{
            conv_id: string;
          }>;
        }
      >({
        query: ({ entityId, params }) => ({
          url: `/api/auth/${entityId}/agents/twilio/message`,
          params: {
            ...params,
            type: "voice",
          },
        }),
        transformResponse: (response: PaginatedResponse<VoiceMessageDto>) => ({
          ...response,
          data: response.data.map(transformVoiceMessageDtoToModel),
        }),
      }),

      getVoiceConversationHistory: builder.query<
        PaginatedResponse<VoiceConversationHistoryItemModel>,
        {
          entityId: string;
          convId: string;
          page: number;
          page_size: number;
        }
      >({
        query: ({ entityId, convId, page, page_size }) => ({
          url: `/api/auth/${entityId}/agents/voice/conversation/${convId}/history`,
          params: { page, page_size },
        }),
        transformResponse: (
          response: PaginatedResponse<VoiceConversationHistoryItemDto>
        ) => ({
          ...response,
          data: response.data.map(
            transformVoiceConversationHistoryItemDtoToModel
          ),
        }),
      }),

      getVoiceMedia: builder.query<Blob, { entityId: string; mediaId: string }>(
        {
          query: ({ entityId, mediaId }) => ({
            url: `/api/${entityId}/agents/voice/media`,
            params: { id: mediaId },
            responseType: "blob",
          }),
        }
      ),

      sendTwilioMessage: builder.mutation<
        TwilioMessageModel,
        WithApiRequestMeta<SendTwilioMessageMutationParams>
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/agents/twilio/message`,
          method: "POST",
          data,
        }),
        transformResponse: (response: TwilioMessageDto) =>
          transformTwilioMessageDtoToModel(response),
      }),

      createVoiceAccount: builder.mutation<
        VoiceAccountModel,
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/agents/voice`,
          method: "POST",
        }),
        onQueryStarted: async ({ entityId }, { dispatch, queryFulfilled }) => {
          try {
            const { data } = await queryFulfilled;

            dispatch(
              integrationsApi.util.updateQueryData(
                "getCRMConfig",
                { entityId },
                (crmConfigDraft) => {
                  crmConfigDraft.voice = data;
                }
              )
            );
          } catch {
            // Create failed — leave CRM config as-is
          }
        },
      }),

      updateVoiceConfig: builder.mutation<
        VoiceAccountModel,
        WithApiRequestMeta<{
          entityId: string;
          data: { metadata: VoiceAccountMetadata };
        }>
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/agents/voice`,
          method: "PUT",
          data,
        }),
        onQueryStarted: (args, { dispatch, queryFulfilled }) => {
          const updateCRMConfigResult = dispatch(
            integrationsApi.util.updateQueryData(
              "getCRMConfig",
              { entityId: args.entityId },
              (crmConfigDraft) => {
                if (!crmConfigDraft.voice) {
                  return crmConfigDraft;
                }

                return {
                  ...crmConfigDraft,
                  voice: {
                    ...crmConfigDraft.voice,
                    metadata: {
                      ...crmConfigDraft.voice.metadata,
                      ...args.data.metadata,
                    },
                  },
                };
              }
            )
          );

          queryFulfilled.catch(() => {
            updateCRMConfigResult.undo();
          });
        },
      }),

      disconnectVoiceAccount: builder.mutation<
        void,
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          method: "DELETE",
          url: `/api/auth/${entityId}/agents/voice`,
        }),
        invalidatesTags: [IntegrationsApiTag.crmConfig],
      }),

      listTwilioPhoneNumbers: builder.mutation<
        TwilioPhoneNumberModel[],
        WithApiRequestMeta<{
          entityId: string;
          sid: string;
          token: string | null;
        }>
      >({
        query: ({ entityId, sid, token }) => ({
          url: `/api/auth/${entityId}/agents/twilio/list-phone-numbers`,
          method: "POST",
          data: { sid, token },
        }),
      }),

      connectTwilioAccount: builder.mutation<
        TwilioAccountModel,
        WithApiRequestMeta<{
          entityId: string;
          sid: string;
          token: string;
          phone_number: string;
          phone_number_sid: string;
        }>
      >({
        query: ({ entityId, sid, token, phone_number, phone_number_sid }) => ({
          url: `/api/auth/${entityId}/agents/twilio/connect`,
          method: "POST",
          data: { sid, token, phone_number, phone_number_sid },
        }),
        invalidatesTags: [IntegrationsApiTag.crmConfig],
      }),

      changeTwilioPhone: builder.mutation<
        TwilioAccountModel,
        WithApiRequestMeta<{
          entityId: string;
          phone_number: string;
          phone_number_sid: string;
        }>
      >({
        query: ({ entityId, phone_number, phone_number_sid }) => ({
          url: `/api/auth/${entityId}/agents/twilio/change-phone`,
          method: "PUT",
          data: { phone_number, phone_number_sid },
        }),
        invalidatesTags: [IntegrationsApiTag.crmConfig],
      }),

      disconnectTwilioAccount: builder.mutation<
        void,
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/agents/twilio/disconnect`,
          method: "DELETE",
        }),
        invalidatesTags: [IntegrationsApiTag.crmConfig],
      }),

      // Email

      disconnectEmailAccount: builder.mutation<
        { success: boolean },
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/agents/email/disconnect`,
          method: "POST",
        }),
        invalidatesTags: [IntegrationsApiTag.crmConfig],
      }),

      updateEmailConfig: builder.mutation<
        EmailAccountModel,
        WithApiRequestMeta<{ entityId: string; data: UpdateEmailConfigPayload }>
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/agents/email/config`,
          method: "PUT",
          data,
        }),
        onQueryStarted: async (
          args,
          { dispatch, queryFulfilled, getState }
        ) => {
          const { ai_reply: aiReply, metadata } = args.data;
          const cachedEmail = integrationsApi.endpoints.getCRMConfig.select({
            entityId: args.entityId,
          })(getState())?.data?.email;
          // Enabling domain auth also returns server-generated domain_records.
          // Don't optimistic-update here — wait for the response so UI never
          // enters "setup" state without those records.
          const isEnablingDomainAuth =
            metadata?.domain_auth_enabled === true &&
            !cachedEmail?.metadata?.domain_auth_enabled;

          const optimisticUpdate = isEnablingDomainAuth
            ? null
            : dispatch(
                integrationsApi.util.updateQueryData(
                  "getCRMConfig",
                  { entityId: args.entityId },
                  (crmConfigDraft) => ({
                    ...crmConfigDraft,
                    email: crmConfigDraft.email
                      ? {
                          ...crmConfigDraft.email,
                          ai_reply: aiReply ?? crmConfigDraft.email.ai_reply,
                          metadata: crmConfigDraft.email.metadata
                            ? {
                                ...crmConfigDraft.email.metadata,
                                ...metadata,
                              }
                            : crmConfigDraft.email.metadata,
                        }
                      : null,
                  })
                )
              );

          try {
            const { data } = await queryFulfilled;

            if (!isEnablingDomainAuth) {
              return;
            }

            dispatch(
              integrationsApi.util.updateQueryData(
                "getCRMConfig",
                { entityId: args.entityId },
                (crmConfigDraft) => {
                  if (!crmConfigDraft.email?.metadata || !data.metadata) {
                    return;
                  }

                  Object.assign(crmConfigDraft.email.metadata, data.metadata);
                }
              )
            );
          } catch {
            optimisticUpdate?.undo();
          }
        },
      }),

      getEmailConfig: builder.query<
        EmailAccountModel,
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/agents/email/config`,
        }),
        onQueryStarted: async ({ entityId }, { dispatch, queryFulfilled }) => {
          try {
            const { data } = await queryFulfilled;

            dispatch(
              integrationsApi.util.updateQueryData(
                "getCRMConfig",
                { entityId },
                (crmConfigDraft) => {
                  crmConfigDraft.email = data;
                }
              )
            );
          } catch {
            // Keep the current CRM config when refresh fails
          }
        },
      }),

      createEmailConfig: builder.mutation<
        EmailAccountModel,
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/agents/email/config`,
          method: "POST",
          data: {
            type: "raw",
          },
        }),
        onQueryStarted: async ({ entityId }, { dispatch, queryFulfilled }) => {
          try {
            const { data } = await queryFulfilled;

            dispatch(
              integrationsApi.util.updateQueryData(
                "getCRMConfig",
                { entityId },
                (crmConfigDraft) => {
                  crmConfigDraft.email = data;
                }
              )
            );
          } catch {
            // Skip
          }
        },
      }),

      sendVerificationEmail: builder.mutation<
        EmailAccountModel,
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/agents/email/send-verification-email`,
          method: "GET",
        }),
        onQueryStarted: async ({ entityId }, { dispatch, queryFulfilled }) => {
          try {
            const { data } = await queryFulfilled;

            dispatch(
              integrationsApi.util.updateQueryData(
                "getCRMConfig",
                { entityId },
                (crmConfigDraft) => {
                  crmConfigDraft.email = data;
                }
              )
            );
          } catch {
            // Keep setup wizard on step 3 when verification fails.
          }
        },
      }),

      getEmailConversations: builder.query<
        PaginatedResponse<EmailConversationModel>,
        WithApiRequestMeta<
          PaginatedRequestParams<{
            entityId: string;
            search_term?: string;
            filter?:
              | CRMConversationFilter.active
              | CRMConversationFilter.closed
              | CRMConversationMode.onAIAutoReply
              | CRMConversationMode.waitingResponse
              | string;
          }>
        >
      >({
        query: ({ entityId, page, page_size, search_term, filter }) => ({
          url: `/api/auth/${entityId}/agents/email/conversation`,
          params: {
            page,
            // the email agent paginates with `size` (other agents use `page_size`)
            size: page_size,
            search_term: search_term?.trim() || undefined,
            filter,
          },
        }),
        transformResponse: (
          response: PaginatedResponse<EmailConversationDto>
        ) => ({
          ...response,
          data: response.data.map(transformEmailConversationDtoToModel),
        }),
      }),

      getEmailConversationById: builder.query<
        EmailConversationModel,
        WithApiRequestMeta<{ entityId: string; convId: string }>
      >({
        query: ({ entityId, convId }) => ({
          url: `/api/auth/${entityId}/agents/email/conversation/${encodeURIComponent(
            convId
          )}`,
        }),
        transformResponse: (response: EmailConversationDto) =>
          transformEmailConversationDtoToModel(response),
        keepUnusedDataFor: 0,
      }),

      getEmailMessages: builder.query<
        PaginatedResponse<EmailMessageModel>,
        WithApiRequestMeta<{
          entityId: string;
          params: PaginatedRequestParams<{
            conv_id: string;
          }>;
        }>
      >({
        query: ({ entityId, params: { page, page_size, conv_id } }) => ({
          url: `/api/auth/${entityId}/agents/email/message`,
          params: {
            page,
            size: page_size,
            conv_id,
          },
        }),
        transformResponse: (response: PaginatedResponse<EmailMessageDto>) => ({
          ...response,
          data: response.data.map(transformEmailMessageDtoToModel),
        }),
      }),

      sendEmailMessage: builder.mutation<
        EmailMessageModel,
        WithApiRequestMeta<SendEmailMessageMutationParams>
      >({
        query: ({ entityId, data: { message_id, name, text, files } }) => {
          const formData = new FormData();
          formData.append("message_id", message_id);
          formData.append("name", name);
          formData.append("text", text);
          files?.forEach((file) => formData.append("files[]", file));

          return {
            url: `/api/auth/${entityId}/agents/email/message`,
            method: "POST",
            data: formData,
            headers: { "Content-Type": "multipart/form-data" },
          };
        },
        transformResponse: (response: EmailMessageDto) =>
          transformEmailMessageDtoToModel(response),
      }),

      getEmailMessageMedia: builder.query<
        Blob,
        WithApiRequestMeta<{ entityId: string; mediaId: string }>
      >({
        query: ({ entityId, mediaId }) => ({
          url: `/api/${entityId}/agents/email/media?id=${mediaId}`,
          responseType: "blob",
        }),
        providesTags: (result, error, { mediaId }) => [
          { type: IntegrationsApiTag.emailMessageMedia, id: mediaId },
        ],
      }),
      getWorkflows: builder.query<
        PaginatedResponse<Workflow>,
        WithApiRequestMeta<PaginatedRequestParams<{ entityId: string }>>
      >({
        query: ({ entityId, meta: _meta, ...params }) => ({
          url: `/api/auth/${entityId}/workflows`,
          params: {
            ...params,
          },
        }),
      }),
      getWorkflow: builder.query<
        Workflow,
        WithApiRequestMeta<{ entityId: string; workflowId: string }>
      >({
        query: ({ entityId, workflowId }) => ({
          url: `/api/auth/${entityId}/workflows/${workflowId}`,
        }),
      }),
      createWorkflow: builder.mutation<
        Workflow,
        WithApiRequestMeta<{ entityId: string; data: WorkflowParams }>
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/workflows`,
          method: "POST",
          data,
        }),
      }),
      updateWorkflow: builder.mutation<
        Workflow,
        WithApiRequestMeta<{
          entityId: string;
          workflowId: string;
          data: Partial<WorkflowParams>;
        }>
      >({
        query: ({ entityId, workflowId, data }) => ({
          url: `/api/auth/${entityId}/workflows/${workflowId}`,
          method: "PUT",
          data,
        }),
        onQueryStarted: (args, { dispatch, queryFulfilled }) => {
          const updateWorkflow = dispatch(
            integrationsApi.util.updateQueryData(
              "getWorkflow",
              {
                entityId: args.entityId,
                workflowId: args.workflowId,
              },
              (updatedWorkflowDraft) => ({
                ...updatedWorkflowDraft,
                ...args.data,
                updated_at: new Date().toISOString(),
              })
            )
          );
          queryFulfilled.catch(() => {
            updateWorkflow.undo();
          });
        },
      }),
      deleteWorkflow: builder.mutation<
        void,
        WithApiRequestMeta<{ entityId: string; workflowId: string }>
      >({
        query: ({ entityId, workflowId }) => ({
          url: `/api/auth/${entityId}/workflows/${workflowId}`,
          method: "DELETE",
        }),
      }),
      getWorkflowLogs: builder.query<
        PaginatedResponse<WorkflowLog>,
        WithApiRequestMeta<
          PaginatedRequestParams<{ entityId: string; workflowId: string }>
        >
      >({
        query: ({ entityId, workflowId, meta: _meta, ...params }) => ({
          url: `/api/auth/${entityId}/workflows/${workflowId}/logs`,
          params: {
            ...params,
          },
        }),
        providesTags: (_result, _error, { workflowId, page }) => [
          {
            type: IntegrationsApiTag.workflowLogs,
            id: `${workflowId}-${page}`,
          },
          { type: IntegrationsApiTag.workflowLogs, id: workflowId },
        ],
      }),
      getAIAgentInsights: builder.query<
        AIAgentInsights,
        WithApiRequestMeta<{
          entityId: string;
          channel: CRMChannel;
          start_time: string;
          end_time: string;
        }>
      >({
        query: ({ entityId, meta: _meta, ...params }) => ({
          url: `/api/auth/${entityId}/agents/stats`,
          params: {
            ...params,
          },
        }),
      }),
      getAIAgentAnalytics: builder.query<
        AIAgentAnalyticsItemModel[],
        WithApiRequestMeta<{ entityId: string; fromISO: string; toISO: string }>
      >({
        query: ({ entityId, fromISO, toISO }) => ({
          url: `/api/auth/${entityId}/cx/agent/analytics`,
          params: {
            from: fromISO,
            to: toISO,
          },
        }),
        transformResponse: (response: { data: AIAgentAnalyticsItemDto[] }) =>
          transformAIAgentAnalyticsItemsDtoToModels(response.data),
      }),
      getCRMUnreadCounts: builder.query<
        CRMUnreadCounts,
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/agents/unread`,
        }),
        providesTags: [IntegrationsApiTag.crmUnreadCounts],
      }),
      getCRMConfig: builder.query<
        CRMConfig,
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/agents/config`,
        }),
        providesTags: [IntegrationsApiTag.crmConfig],
      }),

      // Shopify

      createShopifyConnection: builder.mutation<
        void,
        WithApiRequestMeta<{
          entityId: string;
          data: {
            install_url: string;
            client_id: string;
            client_secret: string;
          };
        }>
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/connections`,
          method: "POST",
          data: {
            type: "shopify",
            name: "Shopify Connection",
            data,
          },
        }),
      }),
      /**
       * @deprecated superseded by `shopifyApi.createShopifyOAuth`
       * (POST /api/shopify/oauth/create), which is common for internal and
       * external flows and handles workspace creation. Kept as a fallback
       * until the new backend endpoint ships.
       * TODO(shopify-billing): retire together with ShopifyInstallDialog and
       * the REVIEW_SHOPIFY_* constants once /shopify/callback fully replaces
       * this flow.
       */
      connectShopifyAccount: builder.mutation<
        Record<string, unknown>,
        WithApiRequestMeta<{
          entityId: string;
          data: {
            type: "shopify";
            name: string;
            data: {
              session_token: string;
              hmac: string;
              store: string;
            };
          };
        }>
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/connections`,
          method: "POST",
          data,
        }),
        invalidatesTags: [IntegrationsApiTag.shopifyAccount],
      }),
      getShopifyAccount: builder.query<
        Record<string, unknown>,
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/agents/shopify/config`,
        }),
        providesTags: [IntegrationsApiTag.shopifyAccount],
      }),
      disconnectShopifyAccount: builder.mutation<
        void,
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          method: "POST",
          url: `/api/auth/${entityId}/agents/shopify/disconnect`,
        }),
        invalidatesTags: [IntegrationsApiTag.shopifyAccount],
      }),

      // Connections (Calendly, Squareup, Calcom)

      getConnections: builder.query<
        {
          data: ConnectionDto[];
          page_number: number;
          page_size: number;
          total_entries: number;
          total_pages: number;
        },
        WithApiRequestMeta<{ entityId: string; type?: ConnectionType }>
      >({
        query: ({ entityId, type }) => ({
          url: `/api/auth/${entityId}/connections`,
          params: type ? { type } : undefined,
        }),
        providesTags: (_result, _error, { type }) => {
          if (!type) {
            return [
              IntegrationsApiTag.calendlyConnection,
              IntegrationsApiTag.squareupConnection,
              IntegrationsApiTag.shopifyAccount,
              IntegrationsApiTag.calcomConnection,
            ];
          }
          if (type === "calendly")
            return [IntegrationsApiTag.calendlyConnection];
          if (type === "squareup")
            return [IntegrationsApiTag.squareupConnection];
          if (type === "cal") return [IntegrationsApiTag.calcomConnection];
          return [IntegrationsApiTag.shopifyAccount];
        },
      }),
      getCalendlyEventTypes: builder.query<
        { data: CalendlyEventType[] },
        WithApiRequestMeta<{ entityId: string; connectionId: string }>
      >({
        query: ({ entityId, connectionId }) => ({
          url: `/api/auth/${entityId}/connections/${connectionId}/event_types`,
        }),
      }),
      getSquareupLocations: builder.query<
        { data: SquareupLocation[] },
        WithApiRequestMeta<{ entityId: string; connectionId: string }>
      >({
        query: ({ entityId, connectionId }) => ({
          url: `/api/auth/${entityId}/connections/${connectionId}/locations`,
        }),
      }),
      getCalcomEventTypes: builder.query<
        { data: CalcomEventType[] },
        WithApiRequestMeta<{ entityId: string; connectionId: string }>
      >({
        query: ({ entityId, connectionId }) => ({
          url: `/api/auth/${entityId}/connections/${connectionId}/event_types`,
        }),
      }),
      setCalcomEventType: builder.mutation<
        { result: string },
        WithApiRequestMeta<{
          entityId: string;
          connectionId: string;
          eventType: CalcomEventType;
        }>
      >({
        query: ({ entityId, connectionId, eventType }) => ({
          method: "PUT",
          url: `/api/auth/${entityId}/connections/${connectionId}/set_event_type`,
          data: { data: { event_type: eventType } },
        }),
        invalidatesTags: [IntegrationsApiTag.calcomConnection],
      }),
      setCalendlyEventType: builder.mutation<
        { result: string },
        WithApiRequestMeta<{
          entityId: string;
          connectionId: string;
          eventType: CalendlyEventType;
        }>
      >({
        query: ({ entityId, connectionId, eventType }) => ({
          method: "PUT",
          url: `/api/auth/${entityId}/connections/${connectionId}/set_event_type`,
          data: { data: { event_type: eventType } },
        }),
        invalidatesTags: [IntegrationsApiTag.calendlyConnection],
      }),
      setSquareupLocation: builder.mutation<
        { result: string },
        WithApiRequestMeta<{
          entityId: string;
          connectionId: string;
          location: SquareupLocation;
        }>
      >({
        query: ({ entityId, connectionId, location }) => ({
          method: "PUT",
          url: `/api/auth/${entityId}/connections/${connectionId}/set_location`,
          data: { data: { location } },
        }),
        invalidatesTags: [IntegrationsApiTag.squareupConnection],
      }),
      deleteConnection: builder.mutation<
        void,
        WithApiRequestMeta<{ entityId: string; connectionId: string }>
      >({
        query: ({ entityId, connectionId }) => ({
          method: "DELETE",
          url: `/api/auth/${entityId}/connections/${connectionId}`,
        }),
        invalidatesTags: [
          IntegrationsApiTag.calendlyConnection,
          IntegrationsApiTag.squareupConnection,
          IntegrationsApiTag.calcomConnection,
          IntegrationsApiTag.shopifyAccount,
        ],
      }),

      // Agent Config

      getAiAgentConfig: builder.query<
        AgentConfigDto,
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/agents/${entityId}/configs`,
        }),
        providesTags: [IntegrationsApiTag.agentConfig],
      }),
      updateAgentConfig: builder.mutation<
        AgentConfigDto,
        WithApiRequestMeta<{ entityId: string; config: AgentConfigData }>
      >({
        query: ({ entityId, config }) => ({
          method: "PUT",
          url: `/api/auth/${entityId}/agents/${entityId}/configs`,
          data: { config },
        }),
        invalidatesTags: [IntegrationsApiTag.agentConfig],
      }),
      executeAgentCurl: builder.mutation<
        unknown,
        WithApiRequestMeta<{
          entityId: string;
          method: string;
          url: string;
          headers: Record<string, string>;
          body: Record<string, unknown>;
        }>
      >({
        query: ({ entityId, meta: _meta, ...data }) => ({
          method: "POST",
          url: `/api/auth/${entityId}/agents/execute-curl`,
          data,
        }),
      }),
    }),
  });

  return integrationsApi;
};

export type IntegrationsApi = ReturnType<typeof addIntegrationsApi>;
